import React from "react";
import { Box, Chip } from "@mui/material";
import { REACTION_OPTIONS } from "../../utils/utils";

// Shared layout primitives for a journal entry's rows — used by both
// JournalEntry (the owner's editable view) and FriendJournalEntry (the
// read-only view of someone else's entry), so the two line up identically.

// Every entry (mobile or desktop, any status) is built from the same fixed
// slots at these fixed offsets, so a stack of entries reads as one aligned
// column rather than drifting with content length or viewport width.
export const LINE_HEIGHT = 24; // one line of controls (icons, stars, small chips)
export const NOTE_LINE_HEIGHT = 20; // one line of body2 text (14px x 1.43)
// Sized to the widest label it must hold — "S12"/"S21" at 25.14px, which
// outgrows the 20px series icon — plus the 8px gap to the status icon.
export const SEASON_GUTTER = 34; // width of the season gutter (merged series+season lists)
export const DATE_COLUMN = 132; // desktop leading gutter — status icon + gap + longest date text
export const CONTENT_INDENT = 32; // mobile leading gutter — status icon width + gap
export const RIGHT_GUTTER = 32; // trailing gutter — matches the more button's 32px tap target
export const ROW_GAP = 1.25; // spacing between stacked rows within a single entry

// The one avatar size used for a person across the app's journal/tracking
// views — the ContentFriends summary card's avatar stacks and a single
// FriendJournalEntry's avatar both use this, so a person reads at the same
// size whether they're in the stack or the list below it.
export const AVATAR_SIZE = { xs: 26, sm: 30 };

/**
 * One row of a journal entry: fixed leading gutter, flexible content, fixed
 * trailing gutter. Every row goes through this, so the three columns stay
 * aligned down the whole list no matter what an individual row contains.
 *
 * `slotHeight` is the height of the row's *first line of content* — what the
 * two gutters centre against. It is deliberately not the row's overall height:
 * rows must hug their content so that the parent Stack's single `spacing` is
 * the entire gap between them. Controls with padding for tap-target reasons
 * (the more button) simply overflow this height symmetrically, staying
 * centred and fully tappable without inflating the vertical rhythm.
 *
 * The content slot is a flex container rather than a plain block: an
 * inline-flex child (MUI's Rating) would otherwise sit on the line box's text
 * baseline and render a couple of pixels high of true centre.
 */
export function EntryRow({
  leadingWidth,
  slotHeight = "auto",
  leading = null,
  trailing = null,
  contentAlign = "center",
  children,
}) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start" }}>
      <Box
        sx={{
          width: leadingWidth,
          flexShrink: 0,
          height: slotHeight,
          display: "flex",
          alignItems: "center",
        }}
      >
        {leading}
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          minHeight: slotHeight,
          display: "flex",
          alignItems: contentAlign,
          gap: 1,
        }}
      >
        {children}
      </Box>
      <Box
        sx={{
          width: RIGHT_GUTTER,
          flexShrink: 0,
          height: slotHeight,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {trailing}
      </Box>
    </Box>
  );
}

// Flex + gap rather than per-chip margins: margins would pad the row above
// and below its chips and break the entry's even vertical rhythm.
export function ReactionChips({ reactions }) {
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1, rowGap: 0.5 }}>
      {reactions.map((reaction, index) => {
        const option = REACTION_OPTIONS.find(
          (option) => option.label === reaction,
        );
        return (
          <Chip
            key={index}
            label={reaction}
            icon={
              option.icon || (
                <span role="img" aria-label={option.label}>
                  {option.emoji}
                </span>
              )
            }
            size="small"
          />
        );
      })}
    </Box>
  );
}
